import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import type { ArkoseBotManagerContext, BotManagerTokenData } from 'bundles/authentication-common/types/BotManagerTypes';

// Registration context values must match register_success eventing schema values. Before adding any new values here,
// ensure they align with the defined schema.
// See https://github.com/webedx-spark/eventing-schemas/blob/main/schemas/eventingv3/entities/user/registration_context.json
export const REGISTRATION_CONTEXT = {
  EOI: 'eoi',
} as const;

export type RegistrationContext = (typeof REGISTRATION_CONTEXT)[keyof typeof REGISTRATION_CONTEXT];

export type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

export type SignupParams = {
  email: string;
  name?: string;
  password: string;
  redirectTo?: string;
  verificationEmailRedirectUrl?: string;
  skipWelcomeEmail?: boolean;
  isPasswordlessSignupEnabled?: boolean;
  registrationContext?: RegistrationContext;
} & Partial<BotManagerTokenData>;

export type ErrorResponse = {
  responseJSON: { errorCode: string };
};

export type SuccessResponse = {
  redirectTo: string;
  userId: number;
};

export type EnterpriseSsoProps = {
  ssoError?: SSOError;
  isEnterpriseSsoLogin?: boolean;
  isLoading?: boolean;
  isRedirecting?: boolean;
  onEmailChange?: (email: string) => void;
  onSubmit?: () => void;
  resetSsoError?: () => void;
};

export type SignupSharedProps = {
  navigate?: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  navigateToLogin: () => void;
  onSuccess: (response: SuccessResponse) => void;
  verificationEmailRedirectUrl?: string;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  enterpriseSsoProps?: EnterpriseSsoProps;
};

export type LoginSharedProps = {
  navigate?: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  defaultEmail?: string;
  onError: (error?: string) => void;
  onForgotPassword: () => void;
  onSSOError?: (error: SSOError) => void;
  onSuccess: (redirectURL: string) => void;
  onRequestMagicLink?: () => void;
  onEmailChange?: (email: string) => void;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  enterpriseSsoProps?: EnterpriseSsoProps;
};

export type LoginValues = {
  email: string;
  password: string;
  'one-time-password'?: string;
};

export type AuthModalContext = Record<string, any>;
